import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { AgentTaskMemoryClickPayload } from "@fencyai/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MemoryChat } from "../../../common/documents/MemoryChat";
import { useFency } from "../../../common/fency/useFency";

type ContractSearchResultModalProps = {
  downloadLink: string;
  initialPage: number;
  memoryId: string;
  memoryTitle: string;
  initialChatPrompt: string;
  onClose: () => void;
};

export const ContractSearchResultModal = ({
  downloadLink,
  initialPage,
  memoryId,
  memoryTitle,
  initialChatPrompt,
  onClose,
}: ContractSearchResultModalProps) => {
  const { t } = useTranslation();
  const { fetchCreateMemoryChatByMemoryIdClientToken } = useFency();
  const [blobUrl, setBlobUrl] = useState<string>();
  const [currentPage, setCurrentPage] = useState(initialPage);
  const blobUrlRef = useRef<string>();

  const fetchClientToken = useCallback(
    () => fetchCreateMemoryChatByMemoryIdClientToken(memoryId),
    [fetchCreateMemoryChatByMemoryIdClientToken, memoryId]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchBlob = async () => {
      const res = await fetch(downloadLink);
      const buf = await res.arrayBuffer();
      const blob = new Blob([buf], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      blobUrlRef.current = url;
      setBlobUrl(url);
    };

    void fetchBlob();

    return () => {
      cancelled = true;
      const u = blobUrlRef.current;
      if (u) {
        URL.revokeObjectURL(u);
        blobUrlRef.current = undefined;
      }
    };
  }, [downloadLink]);

  const iframeSrc = blobUrl ? (currentPage > 0 ? `${blobUrl}#page=${Math.floor(currentPage)}` : blobUrl) : undefined;

  const handleSourceClick = useCallback((payload: AgentTaskMemoryClickPayload) => {
    const sourcePage = payload.pageNumbers[0];
    if (sourcePage && sourcePage > 0) {
      setCurrentPage(Math.floor(sourcePage));
    }
  }, []);

  return (
    <Modal isOpen={true} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        maxW="1500px"
        w="calc(100vw - 2rem)"
        maxH="95vh"
        h="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>{memoryTitle}</ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" minH={0} display="flex" pb="6" overflow="hidden">
          <Flex
            flex="1"
            minH={0}
            minW={0}
            direction={{ base: "column", xl: "row" }}
            gap="4"
            overflow="hidden"
          >
            <Box flex={{ base: 1, xl: 3 }} minH={0} minW={0}>
              <iframe
                key={`pdf-${blobUrl ?? "empty"}-${currentPage}`}
                src={iframeSrc}
                title="PDF preview"
                style={{ width: "100%", height: "100%", minHeight: "240px", border: 0 }}
              />
            </Box>
            <Box
              flex={{ base: 1, xl: 2 }}
              minH={0}
              minW={0}
              display="flex"
              pl={{ base: 0, xl: 4 }}
              pt={{ base: 2, xl: 0 }}
              borderLeftWidth={{ base: 0, xl: "1px" }}
              borderTopWidth={{ base: "1px", xl: 0 }}
              borderColor="smBorder"
              overflow="hidden"
            >
              <MemoryChat
                key={memoryId}
                fetchClientToken={fetchClientToken}
                documentName={memoryTitle}
                emptyStateText={t("Ask something about this document.")}
                placeholderText={t("Ask something about this document…") ?? ""}
                initialPrompt={initialChatPrompt}
                onSourceClick={handleSourceClick}
              />
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
